// AttendanceTabs.tsx
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import React, { FormEvent, useEffect, useState } from "react";
import { STAFF_APIS, StaffAttendanceDTO } from "../../shared/apis/StaffAPI";
import { get, post } from "../../shared/AxiosAPI";
import { getAdminStaff, getGroundStaff, getSecurityStaff, getTeachingFaculties } from "../../shared/Util";
import LoadingSpinner from "../../shared/util/LoadingDialog";
import PageTopBar from "../../../../components/common/PageTopBar";
import { AttendanceMessageStatus } from "./AttendanceMessageStatus";
import { useAttendancePageTabs } from "./AttendancePageTabs";

type FacultyType = "Teaching" | "Janitor" | "Security";

const enum View {
  ALREADY_SUBMITTED = "ALREADY_SUBMITTED",
  CAPTURE = "CAPTURE",
  SPINNER = "SPINNER",
  SUBMITTED = "SUBMITTED",
}

const staffData: Record<FacultyType, StaffAttendanceDTO[]> = {
  Teaching: getTeachingFaculties(),
  Janitor: getGroundStaff(),
  Security: getSecurityStaff(),
};

interface AttendanceEntry {
  name: string;
  mobile: string;
  status: string;
}

/** Staff attendance: Mark Attendance and Message Status tabs. */
const AttendanceTabs: React.FC = () => {
  const [tab, tabs] = useAttendancePageTabs();
  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar left={tabs} />
      {tab === "messages" ? <StaffMessageStatus /> : <MarkStaffAttendance />}
    </Box>
  );
};

/** Messages are not sent to staff yet; the same table as Students is used once they are. */
const StaffMessageStatus: React.FC = () => (
  <>
    <Alert severity="info" sx={{ mb: 2 }}>
      Messages are not sent to staff yet. Once they are, each staff member's message status will be shown here.
    </Alert>
    <AttendanceMessageStatus rows={[]} codeLabel="Staff Id" emptyMessage="No staff messages." />
  </>
);

const MarkStaffAttendance: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTab, setSelectedTab] = useState(0);
  const [staffAttendance, setStaffAttendance] = useState<StaffAttendanceDTO[]>([]);

  const [isAttendanceSubmitted, setIsAttendanceSubmitted] = useState<boolean>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [view, setView] = useState<View>(View.SPINNER);

  const facultyTypes = Object.keys(staffData) as FacultyType[];

  useEffect(() => {
    get(STAFF_APIS.ATTENDANCE, null)
      .then((res) => {
        let alreadySubmitted = res.data["ALREADY_SUBMITTED"];
        if (!alreadySubmitted) {
          //Adding multiple items to state
          setStaffAttendance((prev) => [...prev, ...getTeachingFaculties()]);
          setStaffAttendance((prev) => [...prev, ...getAdminStaff()]);
          setStaffAttendance((prev) => [...prev, ...getGroundStaff()]);
          setStaffAttendance((prev) => [...prev, ...getSecurityStaff()]);
          setView(View.CAPTURE);
        } else {
          let data: { [key: string]: StaffAttendanceDTO[] } = res.data["ABSENTEES_BY_TYPE"];
          console.log(data);
          let absentees: StaffAttendanceDTO[] = [];
          for (const [type, entries] of Object.entries(data)) {
            for (const absentee of entries) {
              absentees.push({
                name: absentee.name,
                phone: absentee.phone,
                attendance: "Absent",
                type: type,
              });
            }
          }
          console.log(absentees);
          setStaffAttendance(absentees);
          setView(View.ALREADY_SUBMITTED);
        }
      })
      .catch((err: AxiosError) => alert(err.message));
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleChange = (phone: number, status: string) => {
    setStaffAttendance((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.phone === phone);
      if (existingIndex === -1) throw Error("Invalid Data");
      // Update existing entry
      const updated = [...prev];
      updated[existingIndex].attendance = status;
      return updated;
    });
  };
  function submitFacultyAttendance(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    let response: Promise<AxiosResponse> = post(STAFF_APIS.ATTENDANCE, null, staffAttendance);
    response
      .then((res) => {
        if (res.status === 201) {
          setView(View.SUBMITTED);
        }
      })
      .catch((err: AxiosError) => alert(err.message));
    console.log(staffAttendance);
  }

  return (
    <>
      {view === View.SPINNER && loading ? <LoadingSpinner open={true} /> : ""}
      {view === View.SUBMITTED ? <Typography>Successfully submitted.</Typography> : <></>}
      {view === View.ALREADY_SUBMITTED ? (
        <>
          <Typography>Attendance for today has already been submitted. The absentees are shown in the table below.</Typography>
          <br />
          <TableContainer className="mui-table-container-style">
            <Table size="small" className="mui-table-bordered">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category </TableCell>
                  <TableCell>Phone No</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staffAttendance?.map((staffAttDto: StaffAttendanceDTO) => (
                  <TableRow key={staffAttDto.phone}>
                    <TableCell>{staffAttDto.name}</TableCell>
                    <TableCell>{staffAttDto.type}</TableCell>
                    <TableCell>{staffAttDto.phone}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
      {view === View.CAPTURE ? (
        <Box sx={{ width: "100%", p: 2 }} component="form" onSubmit={submitFacultyAttendance}>
          {/* Responsive Tabs */}
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            aria-label="Faculty Tabs"
            textColor="primary"
            indicatorColor="primary"
            sx={{ justifyContent: "flex-start" }} // <-- This ensures left alignment
          >
            {facultyTypes.map((type, index) => (
              <Tab key={index} label={type} />
            ))}
          </Tabs>

          {/* Tab Panels */}
          {facultyTypes.map((type, index) => (
            <Box role="tabpanel" hidden={selectedTab !== index} key={type} sx={{ mt: 2 }}>
              {selectedTab === index && (
                <>
                  <Typography variant="h6" gutterBottom>
                    {type} Staff Attendance
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <TableContainer className="mui-table-container-style">
                      <Table size="small" className="mui-table-bordered">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {staffData[type].map((faculty: StaffAttendanceDTO) => (
                            <TableRow>
                              <TableCell>{faculty.name}</TableCell>
                              <TableCell>
                                <FormControl size="small">
                                  <InputLabel
                                    id={"attendance-select-label" + faculty.name}
                                    sx={{
                                      fontSize: { xs: "14px", md: "16px" }, // smaller label
                                    }}
                                    shrink={true} // 👈 Force shrink to prevent overlap
                                  >
                                    Status
                                  </InputLabel>
                                  <Select
                                    labelId={"attendance-select-label" + faculty.name}
                                    id={"attendance-select" + faculty.name}
                                    name={type + "_" + faculty.name + "_attendance"}
                                    label="Status"
                                    defaultValue={"P"}
                                    onChange={(e) => handleChange(faculty.phone, e.target.value)}
                                    sx={{
                                      fontSize: { xs: "14px", md: "16px" },
                                    }}
                                  >
                                    {["P", "A"].map((status: string) => (
                                      <MenuItem
                                        value={status}
                                        key={status}
                                        sx={{
                                          fontSize: { xs: "14px", md: "16px" }, // smaller text
                                        }}
                                      >
                                        {status}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="contained" color="primary" size="small" type="submit">
              Submit All
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography></Typography>
      )}
    </>
  );
};

export default AttendanceTabs;
